import { useEffect, useRef, useState } from "react";
import { Pencil, Check, X, Clock, RefreshCw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import PragmaSelect from "./PragmaSelect";

/**
 * Reusable Details card with Pipedrive-style icon rows and hover-pencil inline edit.
 *
 * Props:
 *  - createdAt, updatedAt (ISO strings)  → shown as read-only muted rows at the top
 *  - fields: Array<FieldConfig>
 *      FieldConfig = {
 *        key: string                 // patch key (e.g. "email")
 *        label: string               // "Email"
 *        value: any                  // current value
 *        icon: LucideIcon
 *        type?: "text"|"email"|"tel"|"url"|"number"|"date"|"select"|"textarea"
 *        placeholder?: string        // input placeholder
 *        options?: [{value,label}]   // for type "select"
 *        editable?: boolean          // default true
 *        display?: (v)=> ReactNode   // custom read-only renderer (e.g. link, badge)
 *        linkTo?: string             // if provided, value renders as router Link
 *        externalHref?: (v)=> string // external URL builder (e.g. website, linkedin)
 *      }
 *  - onSave(patch): Promise<any>   // called with {[key]: value}
 *  - title?: string                 // default "Details"
 *  - testId?: string                // default "details-card"
 */
export default function DetailsCard({
  createdAt,
  updatedAt,
  fields,
  onSave,
  title = "Details",
  testId = "details-card",
}) {
  return (
    <div className="pg-card p-0" data-testid={testId}>
      <div className="px-5 pt-5 pb-3">
        <h3 className="text-sm mono uppercase text-mute tracking-wider">{title}</h3>
      </div>
      <div>
        {createdAt && (
          <MetaRow icon={<Clock size={14} />} label="Created" value={fmtDate(createdAt)} testId={`${testId}-created`} />
        )}
        {updatedAt && (
          <MetaRow icon={<RefreshCw size={14} />} label="Updated" value={fmtDate(updatedAt)} testId={`${testId}-updated`} />
        )}
        {fields.map((f) => (
          <FieldRow key={f.key} field={f} onSave={onSave} testId={`${testId}-field-${f.key}`} />
        ))}
      </div>
    </div>
  );
}


function MetaRow({ icon, label, value, testId }) {
  return (
    <div className="details-row details-row-meta" data-testid={testId}>
      <span className="details-row-icon">{icon}</span>
      <span className="details-row-label">{label}</span>
      <span className="details-row-value mono text-xs" style={{ color: "var(--pg-text-mute)" }}>{value || "—"}</span>
    </div>
  );
}


function FieldRow({ field, onSave, testId }) {
  const {
    key, label, value, icon: Icon, type = "text",
    placeholder, options, editable = true, display, linkTo, externalHref,
  } = field;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      // small delay so React finishes rendering before focus
      setTimeout(() => {
        inputRef.current?.focus();
        if (type !== "select" && type !== "date" && inputRef.current?.select) {
          inputRef.current.select();
        }
      }, 0);
    }
  }, [editing, type]);

  const beginEdit = () => {
    if (!editable) return;
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  const commit = async (override) => {
    // Normalise for comparison
    const oldRaw = value ?? "";
    let newVal = override !== undefined ? override : draft;
    if (type === "number") {
      if (newVal === "" || newVal === null || newVal === undefined) newVal = 0;
      else newVal = Number(newVal);
      if (Number.isNaN(newVal)) { cancel(); return; }
    } else {
      newVal = (newVal ?? "").toString().trim();
    }
    if (String(newVal) === String(oldRaw ?? "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave({ [key]: newVal });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && type !== "textarea") { e.preventDefault(); commit(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  // Read view
  const renderValue = () => {
    const empty = value == null || value === "" || value === 0 && type !== "number";
    if (display) return display(value);
    if (empty && type !== "number") {
      return <span style={{ color: "var(--pg-text-mute)", fontStyle: "italic" }}>—</span>;
    }
    if (type === "email" && value) {
      return <a href={`mailto:${value}`} className="details-value-link" onClick={(e) => e.stopPropagation()}>{value}</a>;
    }
    if (type === "tel" && value) {
      return <a href={`tel:${value}`} className="details-value-link" onClick={(e) => e.stopPropagation()}>{value}</a>;
    }
    if (type === "url" && value) {
      const href = externalHref ? externalHref(value) : (value.startsWith("http") ? value : `https://${value}`);
      return (
        <a href={href} target="_blank" rel="noreferrer noopener" className="details-value-link" onClick={(e) => e.stopPropagation()}>
          {value} <ExternalLink size={11} strokeWidth={2.2} style={{ display: "inline", verticalAlign: "middle", marginLeft: 2, opacity: 0.7 }} />
        </a>
      );
    }
    if (linkTo) {
      return <Link to={linkTo} className="details-value-link" onClick={(e) => e.stopPropagation()}>{value}</Link>;
    }
    if (type === "select" && options) {
      const opt = options.find(o => o.value === value);
      return <span>{opt?.label || value || "—"}</span>;
    }
    return <span>{value}</span>;
  };

  return (
    <div
      className={`details-row ${editing ? "is-editing" : ""} ${editable ? "is-editable" : ""}`}
      data-testid={testId}
      onClick={!editing && editable ? beginEdit : undefined}
      role={editable ? "button" : undefined}
      tabIndex={editable && !editing ? 0 : undefined}
      onKeyDown={(e) => {
        if (!editing && editable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); beginEdit(); }
      }}
    >
      <span className="details-row-icon">{Icon ? <Icon size={14} /> : null}</span>
      <span className="details-row-label">{label}</span>
      <span className="details-row-value">
        {!editing ? (
          renderValue()
        ) : type === "select" ? (
          <PragmaSelect
            value={draft ?? ""}
            onChange={(v) => { setDraft(v); commit(v); }}
            options={options || []}
            testId={`${testId}-input`}
            size="sm"
          />
        ) : type === "textarea" ? (
          <textarea
            ref={inputRef}
            className="details-inline-input"
            rows={3}
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            data-testid={`${testId}-input`}
          />
        ) : (
          <input
            ref={inputRef}
            type={type === "url" ? "url" : type === "number" ? "number" : type === "date" ? "date" : type === "email" ? "email" : type === "tel" ? "tel" : "text"}
            className="details-inline-input"
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            data-testid={`${testId}-input`}
          />
        )}
      </span>
      {!editing && editable && (
        <span className="details-row-edit" aria-hidden="true">
          <Pencil size={12} strokeWidth={2.2} />
          <span>Edit</span>
        </span>
      )}
      {editing && (
        <span className="details-row-edit-actions" onClick={(e) => e.stopPropagation()}>
          {saving ? (
            <span className="details-row-saving mono text-[10px]">saving…</span>
          ) : (
            <>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={commit} title="Save" aria-label="Save" data-testid={`${testId}-save`}>
                <Check size={13} strokeWidth={2.4} />
              </button>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={cancel} title="Cancel" aria-label="Cancel" data-testid={`${testId}-cancel`}>
                <X size={13} strokeWidth={2.4} />
              </button>
            </>
          )}
        </span>
      )}
    </div>
  );
}


function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return (iso || "").slice(0, 10); }
}
